import React, { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { db, auth } from "../firebase";
import { BAREMES, computeMontant } from "../data/baremes";
import andarLogo from "../assets/andar-logo.png";

// Vue direction : la file "a_valider" est suivie en temps réel (onSnapshot),
// en ne lisant QUE les participations à valider, pour rester dans le quota
// gratuit de Firestore même avec des milliers de participations.
// Les compteurs globaux (validées, rejetées, total) viennent du document
// "stats/direction", recalculé par le script Apps Script toutes les 10 min.
// Chaque modification pose "updatedAt", lu par la synchronisation
// incrémentale du Google Sheet.

export default function ValidationQueue() {
  const [participations, setParticipations] = useState([]);
  const [stats, setStats] = useState(null);
  const [correctionOuverte, setCorrectionOuverte] = useState(null);
  const [valeurCorrigee, setValeurCorrigee] = useState("");

  useEffect(() => {
    const q = query(collection(db, "participations"), where("statut", "==", "a_valider"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const liste = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Tri côté navigateur (plus récentes d'abord), sans index composite
      liste.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setParticipations(liste);
    });
    const unsubscribeStats = onSnapshot(doc(db, "stats", "direction"), (snap) => {
      setStats(snap.exists() ? snap.data() : null);
    });
    return () => {
      unsubscribe();
      unsubscribeStats();
    };
  }, []);

  const file = participations;
  const enAttente = participations.length;
  const valide = stats?.nombreValidees ?? "…";
  const rejete = stats?.nombreRejetees ?? "…";
  const montantTotal = stats?.totalValide ?? 0;

  async function valider(p) {
    await updateDoc(doc(db, "participations", p.id), {
      statut: "valide_direction",
      valeurValidee: p.quantiteDeclaree,
      updatedAt: serverTimestamp(),
    });
  }

  async function rejeter(p) {
    await updateDoc(doc(db, "participations", p.id), { statut: "rejete", updatedAt: serverTimestamp() });
  }

  function ouvrirCorrection(p) {
    setCorrectionOuverte(p.id);
    setValeurCorrigee(String(p.quantiteDeclaree));
  }

  async function confirmerCorrection(p) {
    const valeur = parseFloat(valeurCorrigee);
    if (isNaN(valeur) || valeur < 0) return;
    const montant = computeMontant(p.type, valeur, p.nombrePersonnes);
    await updateDoc(doc(db, "participations", p.id), {
      statut: "valide_direction",
      valeurValidee: valeur,
      montant,
      updatedAt: serverTimestamp(),
    });
    setCorrectionOuverte(null);
  }

  return (
    <div className="app-shell">
      <div className="app-header">
        <img src={andarLogo} alt="Logo ANDAR" className="andar-logo" />
        <button className="btn" onClick={() => signOut(auth)}>
          Se déconnecter
        </button>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div className="stat-pill">
          <p className="stat-label">En attente</p>
          <p className="stat-value">{enAttente}</p>
        </div>
        <div className="stat-pill">
          <p className="stat-label">Validé</p>
          <p className="stat-value">{valide}</p>
        </div>
        <div className="stat-pill">
          <p className="stat-label">Rejeté</p>
          <p className="stat-value">{rejete}</p>
        </div>
        <div className="stat-pill accent">
          <p className="stat-label">Total validé</p>
          <p className="stat-value">{montantTotal.toFixed(2).replace(".", ",")} €</p>
        </div>
      </div>

      <div className="card">
        <p style={{ fontSize: 15, fontWeight: 500, margin: "0 0 12px" }}>
          File de validation
        </p>

        {file.length === 0 && (
          <p style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", padding: "1rem 0" }}>
            Aucune participation à valider pour le moment.
          </p>
        )}

        {file.map((p) => {
          const b = BAREMES[p.type];
          return (
            <div
              key={p.id}
              style={{
                background: "var(--surface-alt)",
                border: "1px solid var(--border-color)",
                borderRadius: 12,
                padding: "1rem 1.25rem",
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <p style={{ fontWeight: 500, fontSize: 15, margin: 0 }}>
                  {p.prenom} {p.nom}{" "}
                  <span style={{ fontWeight: 400, fontSize: 12, color: "var(--text-muted)" }}>
                    · {p.departement} ({p.codePostal})
                  </span>
                </p>
                {p.motif && (
                  <span
                    style={{
                      fontSize: 12,
                      background: "#fdf3e0",
                      color: "#8a5a00",
                      padding: "2px 8px",
                      borderRadius: 8,
                    }}
                  >
                    {p.motif}
                  </span>
                )}
              </div>

              <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "6px 0 2px" }}>
                {b.label} déclaré :{" "}
                <span style={{ fontWeight: 500, color: "var(--text-primary)" }}>
                  {p.quantiteDeclaree} {b.unit}
                </span>
              </p>
              <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 12px" }}>
                soit {p.montant?.toFixed(2).replace(".", ",")} € au barème actuel
              </p>

              {p.photoUrls && p.photoUrls.length > 0 && (
                <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                  {p.photoUrls.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer">
                      <img
                        src={url}
                        alt="Preuve envoyée"
                        style={{ height: 64, borderRadius: 6, border: "1px solid var(--border-color)" }}
                      />
                    </a>
                  ))}
                </div>
              )}

              {correctionOuverte === p.id && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                  <input
                    type="number"
                    step="0.1"
                    value={valeurCorrigee}
                    onChange={(e) => setValeurCorrigee(e.target.value)}
                    style={{ width: 100, marginBottom: 0 }}
                  />
                  <button className="btn" onClick={() => confirmerCorrection(p)}>
                    Confirmer
                  </button>
                </div>
              )}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn" onClick={() => valider(p)}>
                  ✓ Valider
                </button>
                <button className="btn" onClick={() => ouvrirCorrection(p)}>
                  ✎ Corriger
                </button>
                <button className="btn" onClick={() => rejeter(p)}>
                  ✕ Rejeter
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
